import React, { useEffect, useRef, useState } from "react";

// This is a public OAuth client identifier. It is safe to ship in the browser;
// the server still verifies every returned ID token before creating a session.
const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_OAUTH_WEB_CLIENT_ID ||
  "442191951373-joq4a6gmsf4vlvgnnjqao8auq4the7tt.apps.googleusercontent.com";
const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";

let googleIdentityScriptPromise;
let googleIdentityInitialized = false;
let activeCredentialHandler = null;

function loadGoogleIdentityServices() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleIdentityScriptPromise) return googleIdentityScriptPromise;

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`
    );
    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Google Sign-In could not be loaded.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_IDENTITY_SCRIPT_URL;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Google Sign-In could not be loaded."));
    document.head.appendChild(script);
  }).then(() => {
    if (!window.google?.accounts?.id) {
      throw new Error("Google Sign-In is unavailable.");
    }
  });

  return googleIdentityScriptPromise;
}

/**
 * Renders Google's official sign-in control.  Google returns an ID token to
 * `onCredential`; callers must pass it to our backend, never decode or trust it
 * in the browser.
 */
function GoogleSignInButton({ onCredential, text = "signin_with" }) {
  const buttonContainerRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const [error, setError] = useState("");

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;
    let handleCredentialResponse = null;
    const buttonContainer = buttonContainerRef.current;

    loadGoogleIdentityServices()
      .then(() => {
        if (cancelled || !buttonContainer) return;

        handleCredentialResponse = async (response) => {
          if (!response?.credential) {
            setError("Google did not return a sign-in credential. Please try again.");
            return;
          }

          setError("");
          try {
            await onCredentialRef.current(response.credential);
          } catch (signInError) {
            setError(signInError?.message || "Google Sign-In could not be completed.");
          }
        };

        // Google advises initializing once per page. The active modal supplies
        // the callback, while the one GIS client safely renders its button in
        // whichever sign-in or account view is currently visible.
        activeCredentialHandler = handleCredentialResponse;
        if (!googleIdentityInitialized) {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_WEB_CLIENT_ID,
            auto_select: false,
            cancel_on_tap_outside: true,
            callback: (response) => activeCredentialHandler?.(response),
          });
          googleIdentityInitialized = true;
        }

        buttonContainer.replaceChildren();
        window.google.accounts.id.renderButton(buttonContainer, {
          type: "standard",
          theme: "outline",
          size: "large",
          text,
          shape: "rectangular",
          logo_alignment: "left",
          width: 210,
        });
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError?.message || "Google Sign-In could not be loaded.");
        }
      });

    return () => {
      cancelled = true;
      if (activeCredentialHandler === handleCredentialResponse) {
        activeCredentialHandler = null;
      }
      if (buttonContainer) {
        buttonContainer.replaceChildren();
      }
    };
  }, [text]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
      <div ref={buttonContainerRef} />
      {error && (
        <div role="alert" style={{ color: "#b3261e", fontSize: "12px", textAlign: "center" }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default GoogleSignInButton;
